import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, X, Users, Package } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AutocompleteInput } from "@/components/AutocompleteInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function PublishTrip() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState({ origin: "", destination: "", date: "", time: "", seats: 3, price: 0, accepts: "passengers", vehicle: "", notes: "", percurso: "" });
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const scrollToError = () => setTimeout(() => {
    document.querySelector('[data-testid^="pub-error-"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 50);

  const Err = ({ k }) => (errors[k] ? <p className="text-xs text-destructive mt-1" data-testid={`pub-error-${k}`}>{errors[k]}</p> : null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const acceptsOptions = [
    { value: "passengers", label: t("publish.passengers"), icon: Users },
    { value: "parcels", label: t("publish.parcels"), icon: Package },
    { value: "both", label: t("publish.both"), icon: null },
  ];

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    const todayStr = localToday();
    if (!form.origin.trim()) errs.origin = t("common.fieldRequired");
    if (!form.destination.trim()) errs.destination = t("common.fieldRequired");
    if (!form.date) errs.date = t("common.fieldRequired");
    else if (form.date < todayStr) errs.date = t("publish.datePast");
    if (!errs.date && form.date === todayStr && form.time && form.time < new Date().toTimeString().slice(0, 5)) errs.time = t("publish.timePast");
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(t("common.checkErrors"));
      scrollToError();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/trips", { ...form, seats: Number(form.seats), price: Number(form.price), stops });
      toast.success(t("publish.success"));
      navigate(`/viagem/${data.id}`);
    } catch (err) {
      const msg = formatApiError(err, t);
      toast.error(msg);
      setErrors({ form: msg });
      scrollToError();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="publish-trip-page">
      <h1 className="text-3xl md:text-4xl font-bold text-primary mb-8">{t("publish.title")}</h1>
      <form onSubmit={submit} noValidate className="bg-white rounded-2xl border border-slate-100 p-6 md:p-10 shadow-sm space-y-6">
        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <Label>{t("publish.origin")} <span className="text-red-500">*</span></Label>
            <AutocompleteInput testid="trip-origin-input" value={form.origin} onChange={(v) => { setForm({ ...form, origin: v }); setErrors((p) => ({ ...p, origin: undefined, form: undefined })); }} placeholder="Lausanne" className="mt-1.5" inputClassName={errors.origin ? "border-destructive h-12 rounded-xl" : "h-12 rounded-xl"} />
            <Err k="origin" />
          </div>
          <div>
            <Label>{t("publish.destination")} <span className="text-red-500">*</span></Label>
            <AutocompleteInput testid="trip-destination-input" value={form.destination} onChange={(v) => { setForm({ ...form, destination: v }); setErrors((p) => ({ ...p, destination: undefined, form: undefined })); }} placeholder="Porto" className="mt-1.5" inputClassName={errors.destination ? "border-destructive h-12 rounded-xl" : "h-12 rounded-xl"} />
            <Err k="destination" />
          </div>
        </div>

        <div>
          <Label>{t("publish.stops")}</Label>
          <div className="space-y-2 mt-1.5">
            {stops.map((s, i) => (
              <div key={i} className="flex gap-2">
                <AutocompleteInput testid={`trip-stop-input-${i}`} value={s} onChange={(v) => setStops(stops.map((x, j) => (j === i ? v : x)))} placeholder={t("publish.stopPlaceholder")} className="flex-1" />
                <Button type="button" variant="ghost" size="icon" data-testid={`trip-stop-remove-${i}`} onClick={() => setStops(stops.filter((_, j) => j !== i))} className="rounded-xl shrink-0"><X size={16} /></Button>
              </div>
            ))}
            <Button type="button" variant="outline" data-testid="trip-add-stop" onClick={() => setStops([...stops, ""])} className="rounded-xl border-dashed">
              <Plus size={16} className="mr-2" />{t("publish.addStop")}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-2">Ex.: Lausanne → Lyon → Bordéus → Vigo → Porto</p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <div>
            <Label htmlFor="date">{t("publish.date")} <span className="text-red-500">*</span></Label>
            <Input id="date" data-testid="trip-date-input" type="date" required min={localToday()} value={form.date} onChange={(e) => { set("date")(e); setErrors((p) => ({ ...p, date: undefined, form: undefined })); }} className={`mt-1.5 h-12 rounded-xl ${errors.date ? "border-destructive" : ""}`} />
            <Err k="date" />
          </div>
          <div>
            <Label htmlFor="time">{t("publish.time")} <span className="text-slate-400 font-normal text-xs">({t("common.optional")})</span></Label>
            <Input id="time" data-testid="trip-time-input" type="time" min={form.date === localToday() ? new Date().toTimeString().slice(0, 5) : undefined} value={form.time} onChange={(e) => { set("time")(e); setErrors((p) => ({ ...p, time: undefined, form: undefined })); }} className={`mt-1.5 h-12 rounded-xl ${errors.time ? "border-destructive" : ""}`} />
            <Err k="time" />
          </div>
        </div>

        <div>
          <Label>{t("publish.accepts")}</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1.5" data-testid="trip-accepts-control">
            {acceptsOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                data-testid={`accepts-${o.value}`}
                onClick={() => setForm({ ...form, accepts: o.value })}
                className={`flex items-center justify-center gap-2 px-4 py-3.5 rounded-xl border text-sm font-semibold transition-colors duration-200 ${form.accepts === o.value ? "bg-primary text-white border-primary" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}
              >
                {o.icon && <o.icon size={16} />}{o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          {(form.accepts === "passengers" || form.accepts === "both") && (
            <div>
              <Label htmlFor="seats">{t("publish.seats")}</Label>
              <Input id="seats" data-testid="trip-seats-input" type="number" min="1" max="8" value={form.seats} onChange={set("seats")} className="mt-1.5 h-12 rounded-xl" />
            </div>
          )}
          <div>
            <Label htmlFor="price">{t("publish.price")}</Label>
            <Input id="price" data-testid="trip-price-input" type="number" min="0" step="0.5" value={form.price} onChange={set("price")} className="mt-1.5 h-12 rounded-xl" />
          </div>
        </div>

        <div>
          <Label htmlFor="vehicle">{t("publish.vehicle")}</Label>
          <Input id="vehicle" data-testid="trip-vehicle-input" value={form.vehicle} onChange={set("vehicle")} placeholder="Peugeot 508" className="mt-1.5 h-12 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="notes">{t("publish.notes")}</Label>
          <Textarea id="notes" data-testid="trip-notes-input" value={form.notes} onChange={set("notes")} rows={3} className="mt-1.5 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="percurso">{t("publish.percurso")}</Label>
          <Input id="percurso" data-testid="trip-percurso-input" value={form.percurso} onChange={set("percurso")} placeholder="Porto → Bordéus → Vigo → Lausanne" className="mt-1.5 h-12 rounded-xl" />
          <p className="text-xs text-slate-400 mt-1.5">{t("publish.percursoHint")}</p>
        </div>

        {errors.form && <p className="text-sm text-destructive bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="pub-error-form">{errors.form}</p>}
        <Button data-testid="trip-submit-button" type="submit" disabled={loading} className="w-full h-12 rounded-full bg-accent hover:bg-accent/90 text-white font-semibold">
          {t("publish.submit")}
        </Button>
      </form>
    </div>
  );
}
